import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  BillingKeys,
  getHubSpotContactsByCompanyId,
  getHubSpotCompanyQuery,
  getBillingInfoModelQuery,
} from '../../../api/billing-api';
import HubspotContactsForm from './hubspot-contacts-form';
import { useState } from 'react';
import AddBillingContactDialog from './add-billing-contact-dialog';
import {
  BillingRoles,
  isAbleToSeeUIElement,
} from '../../../utils/roles/client-roles';
import { useUserSettings } from '../../../utils/use-user-settings';
import { Messages } from '../../../utils/message-constants';
import Box from '@mui/material/Box';
import SharedButton from '../../../shared/buttons/shared-button';
import IconButton from '@mui/material/IconButton';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import PersonAdd from '@mui/icons-material/PersonAdd';
import Refresh from '@mui/icons-material/Refresh';
import SharedTabHeader from '../../../shared/shared-tab-header';
export default function BillingContactsPage() {
  const { billingRoles, changeLogRoles } = useUserSettings();

  const [expanded, setExpanded] = useState<string | false>(false);
  const [openAddContact, setOpenAddContact] = useState(false);
  const queryClient = useQueryClient();

  const {
    isLoading: isLoadingHubSpotCompany,
    isFetching: isFetchingHubSpotCompany,
    data: hubSpotCompany,
  } = useQuery(getHubSpotCompanyQuery());

  const {
    isLoading: isLoadingBillingInfo,
    isFetching: isFetchingBillingInfo,
    data: billingInformationModel,
  } = useQuery(getBillingInfoModelQuery());

  const isEnabled =
    hubSpotCompany && hubSpotCompany.id && billingInformationModel;
  const hubSpotId = hubSpotCompany?.id;

  const canAddContact = isAbleToSeeUIElement(
    billingRoles,
    BillingRoles.canAddContacts.value
  );

  const {
    data: hubSpotContacts,
    isLoading: hubSpotContactsLoading,
    isFetching: hubSpotContactsFetching,
  } = useQuery({
    queryKey: [BillingKeys.HubSpotContacts, hubSpotId],
    queryFn: () => getHubSpotContactsByCompanyId(hubSpotId!),
    enabled: !!isEnabled,
    meta: {
      errorMessage: Messages.Errors.Network.Loading('billing contacts'),
    },
    select: (data) => {
      if (data) {
        data.map((contact) => {
          if (
            contact.properties.email ===
            billingInformationModel?.customerInfoModel?.customerEmail
          ) {
            contact.properties.primary_billing_contact_ = true;
          } else {
            contact.properties.primary_billing_contact_ = false;
          }
          return contact;
        });
        data.sort((a, b) => {
          if (
            a.properties.primary_billing_contact_ &&
            !b.properties.primary_billing_contact_
          )
            return -1;
          if (
            !a.properties.primary_billing_contact_ &&
            b.properties.primary_billing_contact_
          )
            return 1;

          return (a.properties.firstName || '').localeCompare(
            b.properties.firstName || ''
          );
        });
      }
      return data;
    },
  });

  const descriptionText =
    'Billing contacts are users that have been marked within your organization to receive notifications on all invoicing for your company.';

  if (
    isLoadingHubSpotCompany ||
    hubSpotContactsLoading ||
    isFetchingHubSpotCompany ||
    hubSpotContactsFetching ||
    isLoadingBillingInfo ||
    isFetchingBillingInfo
  ) {
    return (
      <Box sx={{ position: 'relative', minHeight: 500 }}>
        <SharedTabHeader title="Billing Contacts">
          {' '}
          <Stack
            direction="row"
            gap={1}
            alignItems="center"
            justifyContent="center"
          >
            <Tooltip arrow title="Refresh">
              <span>
                <IconButton disabled>
                  <Refresh />
                </IconButton>
              </span>
            </Tooltip>
            <SharedButton
              variant="primary"
              onClick={() => setOpenAddContact(true)}
              endIcon={<PersonAdd />}
              disabled
            >
              Add Contact
            </SharedButton>
          </Stack>
        </SharedTabHeader>
        <Typography variant="body1" sx={{ mt: 2, px: 1 }}>
          {descriptionText}
        </Typography>

        <Box sx={{ maxWidth: 1000, mx: 'auto', my: 2 }}>
          {Array.from({ length: 5 }).map((_, index) => (
            <Skeleton
              variant="rectangular"
              height={80}
              key={index}
              sx={{ my: 1 }}
            />
          ))}
        </Box>
      </Box>
    );
  }

  if (hubSpotContacts && hubSpotCompany && billingInformationModel) {
    const filteredContacts = hubSpotContacts.filter(
      (contact) => contact.properties.billing_contact_ !== false
    );
    return (
      <Box sx={{ position: 'relative', minHeight: 500 }}>
        <SharedTabHeader title="Billing Contacts">
          <Stack
            direction="row"
            gap={1}
            alignItems="center"
            justifyContent="center"
          >
            <Tooltip arrow title="Refresh">
              <IconButton
                onClick={() => {
                  queryClient.invalidateQueries({
                    queryKey: [BillingKeys.HubSpotCompany],
                  });
                  queryClient.invalidateQueries({
                    queryKey: [BillingKeys.HubSpotContacts],
                  });

                  queryClient.invalidateQueries({
                    queryKey: [BillingKeys.CustomerBillingInformation],
                  });
                }}
              >
                <Refresh />
              </IconButton>
            </Tooltip>
            <Tooltip
              arrow
              title={
                !canAddContact
                  ? `You need the permission "${BillingRoles.canAddContacts.name}" to add contacts.`
                  : ''
              }
            >
              <span>
                <SharedButton
                  variant="primary"
                  onClick={() => setOpenAddContact(true)}
                  endIcon={<PersonAdd />}
                  disabled={!canAddContact}
                >
                  Add Contact
                </SharedButton>
              </span>
            </Tooltip>
          </Stack>
        </SharedTabHeader>
        <Typography variant="body1" sx={{ mt: 2, px: 1 }}>
          {descriptionText}
        </Typography>

        <Box sx={{ maxWidth: 1000, mx: 'auto', my: 2 }}>
          {filteredContacts.map((contact) => (
            <HubspotContactsForm
              key={contact.id}
              contact={contact}
              billingRoles={billingRoles}
              hubSpotCompany={hubSpotCompany}
              changeLogRoles={changeLogRoles}
              expanded={expanded}
              setExpanded={setExpanded}
              hubSpotContacts={hubSpotContacts}
              billingInformationModel={billingInformationModel}
            />
          ))}
        </Box>

        {openAddContact ? (
          <AddBillingContactDialog
            open={openAddContact}
            handleClose={() => setOpenAddContact(false)}
            hubSpotCompanyId={hubSpotCompany.id}
            changeLogRoles={changeLogRoles}
            billingRoles={billingRoles}
            setOpenAddContact={setOpenAddContact}
            hubSpotContacts={hubSpotContacts}
          />
        ) : null}
      </Box>
    );
  }
}
